// Utility to auto-capitalize (uppercase) string values in Create / Update payloads.
// Also normalizes phone numbers and postal codes to match database consistency.
// Usage: import { autoCapitalizePayload } from "utils/autoCapitalize";
// Then before calling an apiCreate*/apiUpdate* function: payload = autoCapitalizePayload(payload);
// The function skips keys that look like emails or are explicitly excluded.

const DEFAULT_EXCLUDE = new Set([
  // identifiers / keys you do not want to upper-case (key names, all compared in lower-case)
  "email",
  "e_mail",
  "emel",
  "e-mel",
  "username",
  "password",
  "kata_laluan",
  "id",
  "id_pengguna",
  // business-specific: never auto-uppercase account status key
  "status_akaun", // ensures values like "Aktif" / "Tidak Aktif" stay cased
  // (Entries below were previously added but represent values, not keys; kept harmlessly.)
  "aktif",
  "tidak aktif",
]);

// Keys which contain these substrings will be skipped (case-insensitive)
const EXCLUDE_SUBSTRINGS = ["email", "emel", "password", "kata_laluan"];

function shouldSkipKey(key) {
  const lower = key.toLowerCase();
  if (DEFAULT_EXCLUDE.has(lower)) return true;
  return EXCLUDE_SUBSTRINGS.some((s) => lower.includes(s));
}

/**
 * Normalize phone number to XXX-XXXXXXX or XXXX-XXXXXXX format
 */
function normalizePhoneNumber(val) {
  if (typeof val !== "string") return val;
  
  const trimmed = val.trim();
  
  // Keep placeholder
  if (trimmed === "-") return "-";
  
  // Remove all spaces and dashes first
  const digitsOnly = trimmed.replace(/[\s-]/g, "");
  
  // If not all digits, return as is
  if (!/^[0-9]+$/.test(digitsOnly)) return trimmed;
  
  // Format based on length
  if (digitsOnly.length === 10) {
    // XXX-XXXXXXX
    return digitsOnly.substring(0, 3) + "-" + digitsOnly.substring(3);
  } else if (digitsOnly.length === 11) {
    // XXXX-XXXXXXX
    return digitsOnly.substring(0, 4) + "-" + digitsOnly.substring(4);
  }
  
  // Return original if unexpected length
  return trimmed;
}

/**
 * Normalize postal code - trim spaces
 */
function normalizePostalCode(val) {
  if (typeof val !== "string") return val;
  return val.trim();
}

function transformValue(val) {
  if (typeof val !== "string") return val;
  // Skip if it looks like an email
  if (val.includes("@")) return val;
  // Uppercase using locale (Malay) & trim excess surrounding whitespace
  // Also replace multiple spaces with single space
  return val.trim().replace(/\s+/g, " ").toLocaleUpperCase("ms-MY");
}

function normalizeStatusAkaun(val) {
  if (typeof val !== "string") return val;
  const lower = val.trim().toLowerCase();
  if (lower === "aktif") return "Aktif";
  if (lower === "tidak aktif") return "Tidak Aktif";
  return val; // leave unexpected values untouched
}

export function autoCapitalizePayload(input, customExclude = []) {
  if (!input || typeof input !== "object") return input;
  const exclude = new Set(customExclude.map((k) => k.toLowerCase()));

  const recurse = (value) => {
    if (Array.isArray(value)) return value.map(recurse);
    if (value && typeof value === "object") {
      const out = Array.isArray(value) ? [] : {};
      for (const [k, v] of Object.entries(value)) {
        const lowerKey = k.toLowerCase();
        
        // Special handling for status_akaun: keep proper casing
        if (lowerKey === "status_akaun") {
          out[k] = normalizeStatusAkaun(v);
          continue;
        }
        
        // Special handling for phone numbers
        if (lowerKey === "no_tel" || lowerKey === "telefon" || lowerKey === "telefon_waris") {
          out[k] = typeof v === "string" ? normalizePhoneNumber(v) : v;
          continue;
        }
        
        // Special handling for postal codes
        if (lowerKey === "poskod_pelanggan" || lowerKey === "poskod_harta" || lowerKey === "poskod") {
          out[k] = typeof v === "string" ? normalizePostalCode(v) : v;
          continue;
        }
        
        if (shouldSkipKey(k) || exclude.has(lowerKey)) {
          out[k] = v; // untouched
        } else if (typeof v === "string") {
          out[k] = transformValue(v);
        } else if (v && typeof v === "object") {
          out[k] = recurse(v);
        } else {
          out[k] = v;
        }
      }
      return out;
    }
    return value;
  };
  return recurse(input);
}

// Convenience wrapper (alias)
export const normalizeForCreateUpdate = autoCapitalizePayload;

// Example:
// const payload = autoCapitalizePayload(formState, ["remark", "nota"]);
